import React, { useEffect, useMemo } from 'react'
import { Button } from '@mui/material'
import { useCurrentPractice } from '../../contexts/PracticeContext'
import { isPaymentsEnabled, isUSPractice } from 'util/practice'
import { shouldHideReferralProgram } from 'util/consolidator'

/**
 * ReferralProgramButton component that displays the "Join the Referral Program" button.
 *
 * The consolidator's hideReferralProgram flag is the outermost condition: when set, the button
 * is hidden for every practice under that consolidator, even one that force-shows it (DASH-8713).
 *
 * Otherwise visibility is controlled by the isReferralProgramEnabled setting on the practice
 * object, which acts as a per-practice override: explicit `true` force-shows, explicit `false`
 * force-hides.
 *
 * When the flag is unset, the button defaults to ON for any US, payments-enabled practice —
 * consolidator-owned practices included (DASH-8712).
 */
const ReferralProgramButton: React.FC = () => {
	const { currentPracticeObject } = useCurrentPractice()

	const isReferralProgramEnabled = useMemo(() => {
		// Consolidator opt-out wins over a per-practice force-show (DASH-8713)
		if (shouldHideReferralProgram(currentPracticeObject?.consolidator)) {
			return false
		}
		// When the flag is unset, default to ON for US payments-enabled practices (consolidator-owned included)
		return Boolean(
			currentPracticeObject?.settings?.isReferralProgramEnabled ??
			(isPaymentsEnabled(currentPracticeObject) && isUSPractice(currentPracticeObject))
		)
	}, [currentPracticeObject])

	useEffect(() => {
		if (isReferralProgramEnabled) {
			const _dataLayer = (window.dataLayer = window.dataLayer || [])
			_dataLayer.push({
				event: 'partnerButtonReady'
			})
		}
	}, [currentPracticeObject, isReferralProgramEnabled])

	const handleClick = () => {
		const _dataLayer = (window.dataLayer = window.dataLayer || [])
		_dataLayer.push({
			event: 'partnerButtonClicked'
		})
	}

	if (!isReferralProgramEnabled) {
		return null
	}

	return (
		<Button
			id="partnerTrigger"
			fullWidth
			variant="contained"
			color="secondary"
			sx={{
				padding: '4px 15px',
				border: 'none',
				borderRadius: '8px !important',
				fontWeight: '600',
				fontSize: '12px'
			}}
			onClick={handleClick}
			data-cy="referral-program-button"
		>
			Join the Referral Program
		</Button>
	)
}

export default ReferralProgramButton
